import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useEffect, useCallback } from 'react';
import { apiClient } from '../services/apiClient';
import { useAuthToken } from '../features/auth/useAuthToken';

interface PendingJob {
  id: string;
  status: string;
  job_type: string;
  created_at: string;
}

interface JobsResponse {
  jobs: PendingJob[];
}

async function fetchPendingJobs(): Promise<PendingJob[]> {
  const { data } = await apiClient.get<JobsResponse>('/jobs', {
    params: {
      statuses: 'pending,processing',
      limit: 20,
    },
  });
  return data.jobs;
}

/**
 * グローバルなジョブポーリングフック
 * - 進行中のジョブがある間は5秒ごとにポーリング
 * - 進行中のジョブがない場合は60秒ごとにポーリング
 * - 完了を検知したらジョブ一覧を再取得して通知ベルに反映
 */
export function useGlobalJobPolling() {
  const queryClient = useQueryClient();
  const { token } = useAuthToken();
  const previousPendingIdsRef = useRef<Set<string>>(new Set());
  const isInitializedRef = useRef(false);

  const { data: pendingJobs } = useQuery({
    queryKey: ['jobs', 'pending'],
    queryFn: fetchPendingJobs,
    // 未ログイン (公開ホーム閲覧) では 401 を 60 秒ごとに出し続けないよう停止
    enabled: !!token,
    refetchInterval: (query) => {
      const jobs = query.state.data;
      // ジョブがある場合は5秒間隔、ない場合は60秒間隔
      return jobs && jobs.length > 0 ? 5_000 : 60_000;
    },
    staleTime: 0,
  });

  // 完了時のコールバック
  const onJobsCompleted = useCallback(() => {
    // 進行中ジョブリストを即座に再取得
    queryClient.invalidateQueries({ queryKey: ['jobs', 'in-progress'] });
    // ジョブ履歴を再取得 → 通知ベルに反映
    // バックグラウンドスレッドのmetadata書き戻し完了を待つため少し遅延
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['generation', 'jobs'] });
    }, 1500);
  }, [queryClient]);

  // 完了を検知
  useEffect(() => {
    if (!pendingJobs) return;

    const currentPendingIds = new Set(pendingJobs.map((j) => j.id));
    const previousIds = previousPendingIdsRef.current;

    // 初回は完了検知をスキップ（初期化）
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      previousPendingIdsRef.current = currentPendingIds;
      return;
    }

    // 前回は進行中だったが、今回はリストにない = 完了した
    const completedIds: string[] = [];
    previousIds.forEach((id) => {
      if (!currentPendingIds.has(id)) {
        completedIds.push(id);
      }
    });

    if (completedIds.length > 0) {
      onJobsCompleted();
    }

    previousPendingIdsRef.current = currentPendingIds;
  }, [pendingJobs, onJobsCompleted]);

  return { pendingJobs };
}
